import { useStore } from '@nanostores/react';
import { formatDate } from 'date-fns';
import { memo, useEffect, useState } from 'react';
import { SectionRow, SectionContainer } from './SectionContainer';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogActions, DialogButton, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { forkProject } from '~/lib/.client/api/projects';
import { Snapshot } from '~/lib/snapshots/snapshot';
import { snapshotManager } from '~/lib/snapshots/snapshot-manager';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { createScopedLogger } from '~/utils/logger';

const logger = createScopedLogger('Backups');

export const Backups = memo(({}) => {
  const [creatingFork, setCreatingFork] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<Snapshot | undefined>(undefined);
  const projectId = projectStore.getProjectId();
  const { snapshots } = useSnapshots(projectId);

  const showDialog = selectedSnapshot !== undefined;

  async function handleFork() {
    if (!selectedSnapshot) {
      return;
    }

    setCreatingFork(true);

    try {
      await loadSnapshotAsFork(selectedSnapshot);
    } finally {
      setCreatingFork(false);
      setSelectedSnapshot(undefined);
    }
  }

  return snapshots.length > 0 ? (
    <SectionContainer title="Project Backups" className="pb-6">
      {snapshots.map((snapshot) => {
        return (
          <SectionRow
            key={snapshot.messageId}
            title={formatDate(snapshot.createdAt, `d MMMM yyyy 'at' HH:mm`)}
            action={
              <LegacyButton
                minHeight={35}
                className="px-4"
                variant="primary-light"
                onClick={() => setSelectedSnapshot(snapshot)}
              >
                Load
              </LegacyButton>
            }
          />
        );
      })}
      <Dialog
        open={showDialog}
        maxWidth={600}
        showCloseButton={false}
        closeOnBackdropClick={!creatingFork}
        onClose={() => setSelectedSnapshot(undefined)}
      >
        <DialogTitle>Recover to Previous Backup</DialogTitle>
        <DialogDescription>
          <p>You are about to create a project based on a previous state.</p>
          <p className="my-6 bg-alpha-orange-10 border-l border-l-2 border-orange-500 p-2 px-4 text-orange-600 dark:text-orange-500">
            This action will redirect you to the newly created project but your current project will still be reachable
            at the current URL.
          </p>
          <p>Are you sure you want to proceed?</p>
        </DialogDescription>
        <DialogActions>
          <DialogButton variant="secondary" disabled={creatingFork} onClick={() => setSelectedSnapshot(undefined)}>
            Cancel
          </DialogButton>
          <DialogButton variant="warn" disabled={creatingFork} minWidth={30} onClick={handleFork}>
            {creatingFork ? (
              <div className="flex items-center gap-1.5">
                <div className="i-svg-spinners:90-ring-with-bg text-lg" />
                Creating fork
              </div>
            ) : (
              'Create fork'
            )}
          </DialogButton>
        </DialogActions>
      </Dialog>
    </SectionContainer>
  ) : (
    <SectionContainer title="Backups">
      <SectionRow title="Start chatting with Bolt and your backups will show up here" />
    </SectionContainer>
  );
});

async function loadSnapshotAsFork(snapshot: Snapshot) {
  const slug = projectStore.getSlug();

  if (!slug) {
    throw new Error('No slug defined');
  }

  const files = await snapshot.getAsFileMap();

  const { slug: newSlug } = await forkProject({
    slug,
    files,
    activeOrganization: userStore.getActiveOrganization().get(),
  });

  window.location.href = new URL(`/~/${newSlug}`, window.origin).toString();
}

function useSnapshots(projectId?: string) {
  const snapshotsMap = useStore(snapshotManager.snapshots);
  const snapshots = Object.values(snapshotsMap).sort(sortSnapshot);

  useEffect(() => {
    if (!projectId) {
      return undefined;
    }

    const abortController = new AbortController();

    snapshotManager.fetchSnapshots(projectId, abortController.signal).catch((error) => {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      logger.error(error);
    });

    return () => {
      abortController.abort();
    };
  }, [projectId]);

  return {
    snapshots,
  };
}

function sortSnapshot(a: Snapshot, b: Snapshot) {
  return b.createdAt.getTime() - a.createdAt.getTime();
}
